import React, { useCallback, useContext, useEffect, useState } from "react";
import Column from "@amzn/meridian/column";
import Row from "@amzn/meridian/row";
import Button from "@amzn/meridian/button";
import Table, { TableCell, TableRow } from "@amzn/meridian/table";
import "../styles/OpsClockView.css";
import Text from "@amzn/meridian/text";
import { FormattedMessage } from "react-intl";
import { SettingsContext } from "./SettingsContext";
import BreaksData from "./BreaksData";
import UpdatePlanResponse from "./UpdatePlanResponse";
import Input from "@amzn/meridian/input";
import TimePicker from "rc-time-picker";
import "rc-time-picker/assets/index.css";
import Modal, { ModalFooter } from "@amzn/meridian/modal";
import { getStationCode } from "../../network/NetworkUtil";
import DatePicker from "@amzn/meridian/date-picker";
import { validateTimes, checkIfIntervalsAreValid } from "../../util/Util";
import RadioButton from "@amzn/meridian/radio-button";
import { startTime } from "../SettingsConstant";
import { endTime } from "./SettingsConstant";

const momentTimezone = require("moment-timezone");
const MINUTES_IN_AN_HOUR = 60;

const Breaks = (props) => {
  const { viewModel, dispatch } = useContext(SettingsContext);
  const [oldOpsClockData, setOldOpsClockData] = useState({});
  const [newOpsClockData, setNewOpsClockData] = useState({});
  const [timeZone, setTimeZone] = useState(null);
  const [currentDate, setCurrentDate] = useState(
    viewModel.currentBreaksDate
      ? viewModel.currentBreaksDate
      : new Date().toISOString(),
  );
  const [newBreakName, setNewBreakName] = useState(null);
  const [newBreakStartTime, setNewBreakStartTime] = useState(null);
  const [newBreakEndTime, setNewBreakEndTime] = useState(null);
  const [newBreakType, setNewBreakType] = useState("UNPAID");
  const [openNewBreakModal, setOpenNewBreakModal] = useState(false);
  const onNewBreakModalClose = useCallback(
    () => setOpenNewBreakModal(false),
    [],
  );
  const onDeleteBreakModalClose = useCallback(
    () => dispatch({ type: "UPDATE_DELETE_BREAK_DATA" }),
    [],
  );
  const [errorMessagesForAddNewBreak, setErrorMessagesForAddNewBreak] =
    useState({
      errorMessageForBreakName: "",
      errorMessageForTimeIntervals: "",
    });
  const defaultDate = "1970-01-01";
  const opsClock = JSON.parse(JSON.stringify(newOpsClockData));
  const isUserAuthorisedForSettingsEdit =
    props.props.isUserAuthorisedForSettingsEdit;
  const [updatedBreaksData, setUpdatedBreaksData] = useState([]);
  const onAddNewBreakClickButton = useCallback(() => {
    setOpenNewBreakModal(true);
    setNewBreakName("");
    setNewBreakStartTime("");
    setNewBreakEndTime("");
    setErrorMessagesForAddNewBreak({
      errorMessageForBreakName: "",
      errorMessageForTimeIntervals: "",
    });
  }, []);

  const handleChangeDispatchDate = (newDate) => {
    setCurrentDate(newDate);
    dispatch({
      type: "LOAD_BREAKS_DATA",
      data: { dispatcher: dispatch, dispatchDate: newDate },
    });
  };

  const createNewOpsClock = () => {
    let opsClock = {
      nodeId: getStationCode(),
      dispatchDate: defaultDate,
    };

    setOldOpsClockData(opsClock);
    setNewOpsClockData(opsClock);
  };

  const onBreaksChange = (value, type) => {
    if (value) {
      type === startTime
        ? setNewBreakStartTime(value.format("HH:mm"))
        : setNewBreakEndTime(value.format("HH:mm"));
    }
  };

  useEffect(() => {
    if (viewModel.breaksData.opsClock) {
      const oldOpsClockBreaksData = JSON.parse(
        JSON.stringify(viewModel.breaksData.opsClock),
      );
      // We will not store updated ops clock events in the record created just for breaks.
      const newOpsClockBreaksData = JSON.parse(
        JSON.stringify(oldOpsClockBreaksData),
      );
      oldOpsClockBreaksData.dispatchDate = defaultDate;
      newOpsClockBreaksData.dispatchDate = defaultDate;
      setOldOpsClockData(oldOpsClockBreaksData);
      setNewOpsClockData(newOpsClockBreaksData);
    } else {
      createNewOpsClock();
    }

    const timeZone = viewModel.breaksData.nodeTimeZone
      ? viewModel.breaksData.nodeTimeZone
      : Intl.DateTimeFormat().resolvedOptions().timeZone;

    setTimeZone(timeZone);
  }, [viewModel.breaksData.opsClock]);

  const handleBreakTimeOverride = (
    newTime,
    oldEpoch,
    index,
    datetime,
    timeType,
  ) => {
    const newDateString = viewModel.currentBreaksDate + " " + newTime;
    let newDate = new Date(newDateString);
    let oldDate = new Date(datetime);

    let newEpoch;

    newEpoch = oldEpoch + (newDate.getTime() - oldDate.getTime()) / 1000;

    let updatedBreakTime =
      validateTimes(
        new Date(newEpoch * 1000),
        currentDate,
        viewModel.breaksData,
      ) / 1000;

    const localTimeIntervals =
      opsClock.breaks[index].localTimeIntervals &&
      opsClock.breaks[index].localTimeIntervals[0]
        ? opsClock.breaks[index].localTimeIntervals
        : [{}];
    // Validate times function will return NAN if the break is invalid and lies outside the (induct/sort - dispatch) range.
    // If that's the case then we will fall back to the old break time to prevent the override time to be stored as NAN once clicked on save.
    if (timeType === "startTime") {
      if (!isNaN(updatedBreakTime)) {
        localTimeIntervals[0].startTime = {
          hour: newDate.getHours(),
          minute: newDate.getMinutes(),
        };
        opsClock.breaks[index].localTimeIntervals = localTimeIntervals;
        opsClock.breaks[index].offsetFromDispatchDate =
          new Date(newEpoch * 1000).getDate() ===
          new Date(updatedBreakTime * 1000).getDate()
            ? 0
            : -1;
      }
      updatedBreakTime = isNaN(updatedBreakTime)
        ? opsClock.breaks[index].timeIntervals[0].startTime
        : updatedBreakTime;
      opsClock.breaks[index].timeIntervals[0].startTime =
        newTime === "" ? null : updatedBreakTime;
    } else {
      if (!isNaN(updatedBreakTime)) {
        localTimeIntervals[0].endTime = {
          hour: newDate.getHours(),
          minute: newDate.getMinutes(),
        };
        opsClock.breaks[index].localTimeIntervals = localTimeIntervals;
      }
      updatedBreakTime = isNaN(updatedBreakTime)
        ? opsClock.breaks[index].timeIntervals[0].endTime
        : updatedBreakTime;
      opsClock.breaks[index].timeIntervals[0].endTime =
        newTime === "" ? null : updatedBreakTime;
    }
  };

  const handleUpdateBreaks = () => {
    updatedBreaksData.forEach((ubd, index) => {
      Object.keys(ubd).forEach((key) => {
        let timeObj = ubd[key];
        handleBreakTimeOverride(
          timeObj["newTime"],
          timeObj["currentTime"],
          index,
          timeObj["currentDateTimeString"],
          timeObj["timeType"],
        );
      });
    });

    dispatch({
      type: "UPDATE_BREAKS_REQUEST",
      data: {
        dispatcher: dispatch,
        planData: updateBreaksWithDefaultDispatchDate(opsClock, timeZone),
        showUpdateBreakModal: true,
      },
    });
    setUpdatedBreaksData([]);
    setNewOpsClockData(opsClock);
  };

  const handleAddNewBreak = () => {
    if (!newBreakName || newBreakName === "") {
      setErrorMessagesForAddNewBreak({
        ...errorMessagesForAddNewBreak,
        errorMessageForBreakName: "Please enter break name",
      });
      return;
    }
    if (!newBreakStartTime || !newBreakEndTime) {
      setErrorMessagesForAddNewBreak({
        ...errorMessagesForAddNewBreak,
        errorMessageForTimeIntervals: "Please enter both start and end times",
        errorMessageForBreakName: "",
      });
      return;
    }

    const date = defaultDate.split("T")[0];
    let newOpsClock = JSON.parse(JSON.stringify(newOpsClockData));
    newOpsClock.dispatchDate = date;
    const localStartDatetimeString = currentDate + " " + newBreakStartTime;
    const localEndDatetimeString = currentDate + " " + newBreakEndTime;
    let startDate = new Date(localStartDatetimeString);
    let endDate = new Date(localEndDatetimeString);

    let timeZoneConvertedStartDate = new Date(
      startDate.toLocaleString("en-US", { timeZone }),
    );
    let timeZoneStartTimeOffset =
      startDate.getTime() - timeZoneConvertedStartDate.getTime();
    let newStartDateTimeZone = new Date(
      startDate.getTime() + timeZoneStartTimeOffset,
    );

    let timeZoneConvertedEndDate = new Date(
      endDate.toLocaleString("en-US", { timeZone }),
    );
    let timeZoneEndTimeOffset =
      endDate.getTime() - timeZoneConvertedEndDate.getTime();
    let newEndDateTimeZone = new Date(
      endDate.getTime() + timeZoneEndTimeOffset,
    );

    startDate = validateTimes(
      newStartDateTimeZone,
      currentDate,
      viewModel.breaksData,
    );
    endDate = validateTimes(
      newEndDateTimeZone,
      currentDate,
      viewModel.breaksData,
    );

    if (!checkIfIntervalsAreValid(startDate, endDate)) {
      setErrorMessagesForAddNewBreak({
        ...errorMessagesForAddNewBreak,
        errorMessageForTimeIntervals: "Please enter valid start and end times",
        errorMessageForBreakName: "",
      });
      return;
    }

    let newBreak = {
      breakName: newBreakName,
      timeIntervals: [
        {
          startTime: startDate.getTime() / 1000,
          endTime: endDate.getTime() / 1000,
        },
      ],
      breakType: newBreakType,
      localTimeIntervals: [
        {
          startTime: {
            hour: new Date(localStartDatetimeString).getHours(),
            minute: new Date(localStartDatetimeString).getMinutes(),
          },
          endTime: {
            hour: new Date(localEndDatetimeString).getHours(),
            minute: new Date(localEndDatetimeString).getMinutes(),
          },
        },
      ],
      offsetFromDispatchDate:
        newStartDateTimeZone.getDate() === startDate.getDate() ? 0 : -1,
    };

    let breaks;
    if (viewModel.breaksData.opsClock && viewModel.breaksData.opsClock.breaks) {
      breaks = [...viewModel.breaksData.opsClock.breaks, newBreak];
    } else {
      breaks = [newBreak];
    }

    newOpsClock.breaks = breaks;
    dispatch({
      type: "UPDATE_BREAKS_REQUEST",
      data: {
        dispatcher: dispatch,
        planData: updateBreaksWithDefaultDispatchDate(newOpsClock, timeZone),
        showUpdateBreakModal: false,
      },
    });
    onNewBreakModalClose();
    setNewBreakName("");
    setNewBreakStartTime(null);
    setNewBreakEndTime(null);
  };

  const handleBreakDelete = (index) => {
    let newOpsClock = JSON.parse(JSON.stringify(newOpsClockData));
    newOpsClock.breaks.splice(index, 1);
    dispatch({
      type: "UPDATE_BREAKS_REQUEST",
      data: {
        dispatcher: dispatch,
        planData: updateBreaksWithDefaultDispatchDate(newOpsClock, timeZone),
        showUpdateBreakModal: false,
      },
    });
    dispatch({ type: "UPDATE_DELETE_BREAK_DATA" });
  };

  const renderDeleteBreakModal = () => {
    const { openDeleteBreakModal, indexOfTheBreakToDelete } =
      viewModel.deleteBreakData;
    let breakName =
      indexOfTheBreakToDelete != null
        ? newOpsClockData.breaks[indexOfTheBreakToDelete].breakName
        : null;

    return (
      <React.Fragment>
        {breakName && (
          <Modal
            title="Delete Break"
            open={openDeleteBreakModal}
            onClose={onDeleteBreakModalClose}
            scrollContainer="viewport"
            closeLabel="Close"
            describedById="modal-description"
          >
            <Text id="modal-description">
              <FormattedMessage
                id="settings.opsClock.modal.deleteRuleWarning"
                defaultMessage="Are you sure you want to delete {breakName} break?"
                values={{ breakName }}
              />{" "}
            </Text>
            <ModalFooter>
              <Row alignmentHorizontal="right" widths="fit">
                <Button
                  type="secondary"
                  size="small"
                  onClick={onDeleteBreakModalClose}
                >
                  <FormattedMessage
                    id="settings.opsClock.modal.cancel"
                    defaultMessage="Cancel"
                  />
                </Button>
                <Button
                  type="primary"
                  size="small"
                  onClick={() => handleBreakDelete(indexOfTheBreakToDelete)}
                >
                  <FormattedMessage
                    id="settings.opsClock.modal.confirm"
                    defaultMessage="Confirm"
                  />
                </Button>
              </Row>
            </ModalFooter>
          </Modal>
        )}
      </React.Fragment>
    );
  };

  return (
    <Column spacing="small" className="opsClock">
      <Row
        spacingInset="small 400"
        backgroundColor="secondary"
        width={800}
        className="breakSaveButton"
      >
        <DatePicker
          value={currentDate}
          onChange={handleChangeDispatchDate}
          label=""
          width={170}
        />
        <div>
          <Button
            className="breaksFilterButton breaks"
            onClick={onAddNewBreakClickButton}
            disabled={!isUserAuthorisedForSettingsEdit}
          >
            Add
          </Button>
          <Button
            onClick={handleUpdateBreaks}
            className="breaks"
            disabled={
              oldOpsClockData.breaks == null || !isUserAuthorisedForSettingsEdit
            }
          >
            Save
          </Button>
        </div>
      </Row>
      <Row width={565}>
        {oldOpsClockData ? (
          <Table
            headerRows={1}
            showStripes={false}
            showDividers={true}
            spacing="small"
          >
            {newOpsClockData && newOpsClockData.breaks && (
              <TableRow>
                <TableCell>Break</TableCell>
                <TableCell>Category</TableCell>
                <TableCell width={100}>Scheduled Start Time</TableCell>
                <TableCell width={170}>Override Start Time</TableCell>
                <TableCell width={100}>Scheduled End Time</TableCell>
                <TableCell width={165}>Override End Time</TableCell>
              </TableRow>
            )}

            {newOpsClockData
              ? newOpsClockData.breaks
                ? newOpsClockData.breaks.map((breakEvent, bkIndex) => {
                    return breakEvent.timeIntervals.map((ti, tiIndex) => {
                      let oldStartTime =
                        oldOpsClockData.breaks[bkIndex].timeIntervals[0]
                          .startTime;
                      let oldEndTime =
                        oldOpsClockData.breaks[bkIndex].timeIntervals[0]
                          .endTime;
                      return [
                        <TableRow key={Math.random()}>
                          <BreaksData
                            breakEvent={breakEvent}
                            currentStartTime={ti.startTime}
                            currentEndTime={ti.endTime}
                            oldStartTime={oldStartTime}
                            oldEndTime={oldEndTime}
                            breakType={breakEvent.breakType}
                            timeZone={timeZone}
                            dispatchDate={newOpsClockData.dispatchDate}
                            index={bkIndex}
                            handleBreakDelete={handleBreakDelete}
                            updatedBreaks={updatedBreaksData}
                            isUserAuthorisedForSettingsEdit={
                              isUserAuthorisedForSettingsEdit
                            }
                          />
                        </TableRow>,
                      ];
                    });
                  })
                : "No breaks data found"
              : "No opsClock data found"}
          </Table>
        ) : (
          <Text color={"secondary"}>
            <FormattedMessage
              id="opsClock"
              defaultMessage={"No breaks data found"}
            />
          </Text>
        )}
      </Row>
      <UpdatePlanResponse />

      <Modal
        title="New Break"
        open={openNewBreakModal}
        onClose={onNewBreakModalClose}
        scrollContainer="viewport"
        closeLabel="Close"
        describedById="modal-description"
      >
        <Row className="breakContainer">
          <Column spacing="400">
            <Column spacing="200">
              <Text
                tag="label"
                type="b200"
                color="primary"
                htmlFor="nameOfShift"
              >
                Name of Break
              </Text>
              <Input
                id="nameOfBreak"
                type="text"
                placeholder="Break name"
                size="small"
                onChange={setNewBreakName}
                value={newBreakName}
                width={300}
              />
            </Column>
            {errorMessagesForAddNewBreak.errorMessageForBreakName !== "" && (
              <Text className="addBreakError">
                {errorMessagesForAddNewBreak.errorMessageForBreakName}
              </Text>
            )}
            <Row>
              <Column spacing="100">
                <RadioButton
                  checked={newBreakType === "UNPAID"}
                  onChange={setNewBreakType}
                  name="breakType"
                  value="UNPAID"
                >
                  Unpaid
                </RadioButton>
              </Column>
              <Column spacing="100">
                <RadioButton
                  checked={newBreakType === "PAID"}
                  onChange={setNewBreakType}
                  name="breakType"
                  value="PAID"
                >
                  Paid
                </RadioButton>
              </Column>
            </Row>
            <Row>
              <Column spacing="100">
                <Text
                  tag="label"
                  type="b200"
                  color="primary"
                  htmlFor="startTime"
                >
                  Start Time
                </Text>
                <TimePicker
                  showSecond={false}
                  timeOnly={true}
                  minuteStep={15}
                  width={200}
                  placeholder="HH:MM"
                  onChange={(time) => onBreaksChange(time, startTime)}
                  focusOnOpen={true}
                />
              </Column>

              <Column spacing="100">
                <Text tag="label" type="b200" color="primary" htmlFor="endTime">
                  End Time
                </Text>
                <TimePicker
                  showSecond={false}
                  timeOnly={true}
                  minuteStep={15}
                  width={200}
                  placeholder="HH:MM"
                  onChange={(time) => onBreaksChange(time, endTime)}
                  focusOnOpen={true}
                />
              </Column>
            </Row>
            {errorMessagesForAddNewBreak.errorMessageForTimeIntervals !==
              "" && (
              <Text className="addBreakError">
                {errorMessagesForAddNewBreak.errorMessageForTimeIntervals}
              </Text>
            )}
          </Column>
        </Row>
        <Row className="saveNewBreakButton">
          <Button type="secondary" onClick={onNewBreakModalClose}>
            Cancel
          </Button>
          <Button onClick={handleAddNewBreak}>Save</Button>
        </Row>
      </Modal>
      {renderDeleteBreakModal()}
    </Column>
  );
};

const updateBreaksWithDefaultDispatchDate = (opsClock, timezone) => {
  if (Array.isArray(opsClock.breaks)) {
    opsClock.breaks.forEach((_, index) => {
      const { startTime: startTimeEpoch, endTime: endTimeEpoch } =
        opsClock.breaks[index].timeIntervals[0];
      const startTimeMoment = momentTimezone.unix(startTimeEpoch).tz(timezone);
      const endTimeMoment = momentTimezone.unix(endTimeEpoch).tz(timezone);
      startTimeMoment.year(1970);
      startTimeMoment.month(0);
      startTimeMoment.date(1);
      endTimeMoment.year(1970);
      endTimeMoment.month(0);
      endTimeMoment.date(1);
      if (
        startTimeMoment.hours() * MINUTES_IN_AN_HOUR +
          startTimeMoment.minutes() >
        endTimeMoment.hours() * MINUTES_IN_AN_HOUR + endTimeMoment.minutes()
      ) {
        startTimeMoment.subtract(1, "days");
      }
      opsClock.breaks[index].timeIntervals[0].startTime =
        startTimeMoment.unix();
      opsClock.breaks[index].timeIntervals[0].endTime = endTimeMoment.unix();
    });
  }
  return opsClock;
};

export default Breaks;



// WEBPACK FOOTER //
// ./src/components/settings/settingsv2/Breaks.js